import { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CreditCard, Lock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Load Stripe outside of component to avoid recreating on renders
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    const response = await axios.get(`${API}/stripe/config`);
    stripePromise = loadStripe(response.data.publishable_key);
  }
  return stripePromise;
};

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#1c1917',
      '::placeholder': {
        color: '#a8a29e',
      },
      fontFamily: 'Inter, system-ui, sans-serif',
    },
    invalid: {
      color: '#dc2626',
      iconColor: '#dc2626',
    },
  },
  hidePostalCode: false,
};

function PaymentForm({ onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [cardComplete, setCardComplete] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Create setup intent
      const { data: setupData } = await axios.post(
        `${API}/stripe/setup-intent`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Confirm card setup
      const { error, setupIntent } = await stripe.confirmCardSetup(
        setupData.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (error) {
        toast.error(error.message);
        onError?.(error.message);
      } else if (setupIntent.status === 'succeeded') {
        // Start the trial
        const { data: trialData } = await axios.post(
          `${API}/stripe/start-trial`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success(trialData.message || 'Your 60-day free trial has started!');
        onSuccess?.(trialData);
      }
    } catch (err) {
      console.error('Payment setup error:', err);
      const message = err.response?.data?.detail || 'Failed to set up payment method';
      toast.error(message);
      onError?.(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border border-stone-200 rounded-lg bg-white">
        <CardElement
          options={cardElementOptions}
          onChange={(e) => setCardComplete(e.complete)}
        />
      </div>
      
      <div className="flex items-center gap-2 text-sm text-stone-500">
        <Lock className="w-4 h-4" />
        <span>Your card is securely stored by Stripe. You won&apos;t be charged until your trial ends.</span>
      </div>

      <Button
        type="submit"
        disabled={!stripe || loading || !cardComplete}
        className="w-full h-11 rounded-full bg-[#5F8D4E] text-white hover:bg-[#4a7340] font-medium disabled:opacity-50"
      >
        {loading ? (
          'Processing...'
        ) : (
          <>
            <CreditCard className="w-4 h-4 mr-2" />
            Start 60-Day Free Trial
          </>
        )}
      </Button>
    </form>
  );
}

export default function PaymentSetup({ onSuccess, onError, showCard = true }) {
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeInstance, setStripeInstance] = useState(null);

  useEffect(() => {
    const initStripe = async () => {
      try {
        const stripe = await getStripePromise();
        setStripeInstance(stripe);
        setStripeReady(true);
      } catch (err) {
        console.error('Failed to load Stripe:', err);
        toast.error('Failed to load payment system');
      }
    };
    initStripe();
  }, []);

  if (!showCard) {
    return null;
  }

  return (
    <Card className="border-stone-200 shadow-sm">
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-stone-900">Add Payment Method</h3>
            <p className="text-sm text-stone-600">Start your 60-day free trial</p>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div className="text-sm text-green-800">
              <p className="font-medium">60 Days Free!</p>
              <p>Your card won&apos;t be charged until your trial ends. Cancel anytime.</p>
            </div>
          </div>
        </div>

        {stripeReady && stripeInstance ? (
          <Elements stripe={stripeInstance}>
            <PaymentForm onSuccess={onSuccess} onError={onError} />
          </Elements>
        ) : (
          <div className="h-24 flex items-center justify-center">
            <p className="text-stone-500">Loading payment form...</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
